import React from "react";
import { useController, useFormContext } from "react-hook-form";

const InputNumber = ({ id, default_value, min, max, disabled = false }) => {
  const {
    field: { onChange, onBlur, value, name },
  } = useController({
    name: id,
    defaultValue: default_value,
  });
  const { clearErrors } = useFormContext();

  const lowerBound = typeof min === "number" ? min : 0;
  const upperBound = typeof max === "number" ? max : Infinity;
  const clamp = (n) => Math.min(upperBound, Math.max(lowerBound, n));

  // Le store ne contient jamais "" ni NaN. On garde une copie "texte" pour
  // autoriser l'affichage transitoire d'un champ vide (le temps de retaper),
  // sans jamais propager une valeur non numerique aux composants qui la lisent
  // (ex: RoomTab fait `[...Array(nbr_rooms)]` a chaque frappe).
  const [text, setText] = React.useState(
    typeof value === "number" ? String(value) : ""
  );

  // Resynchronise l'affichage quand la valeur change de l'exterieur
  // (boutons +/-, reset du formulaire, pre-remplissage en mode edition),
  // mais jamais pendant que l'utilisateur tape dans ce champ.
  React.useEffect(() => {
    if (typeof value === "number" && document.activeElement?.name !== name) {
      setText(String(value));
    }
  }, [value, name]);

  const commit = (n) => {
    const next = clamp(n);
    setText(String(next));
    onChange(next);
  };

  const handleChange = (e) => {
    clearErrors();
    const raw = e.target.value;
    if (raw === "") {
      // Champ vide autorise a l'ecran, mais le store garde le dernier nombre valide.
      setText("");
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    commit(n);
  };

  const handleBlur = (e) => {
    // A la sortie du champ, on normalise l'affichage (vide -> min).
    const n = parseInt(e.target.value, 10);
    commit(Number.isNaN(n) ? lowerBound : n);
    onBlur(e);
  };

  const handleDecrease = (e) => {
    if (disabled) return;
    clearErrors();
    e.preventDefault();
    commit((typeof value === "number" ? value : lowerBound) - 1);
  };

  const handleIncrease = (e) => {
    if (disabled) return;
    clearErrors();
    e.preventDefault();
    commit((typeof value === "number" ? value : lowerBound) + 1);
  };

  return (
    <div className="flex border border-gray-200 h-value-30 rounded-md">
      <button
        disabled={disabled || value === min}
        className={`group w-value-30 bg-grey-pagination rounded-l-md text-fs-26 flex items-center justify-center ${
          disabled || value <= min ? "disabled" : ""
        }`}
        onClick={handleDecrease}
      >
        <span className="group-[.disabled]:opacity-20">-</span>
      </button>
      <input
        onChange={handleChange}
        onBlur={handleBlur}
        value={text}
        name={name}
        type="number"
        readOnly={disabled}
        className={`w-value-30 text-center outline-0 text-fs-14 ${
          disabled ? "bg-gray-100 cursor-not-allowed" : ""
        }`}
      />
      <button
        disabled={disabled || value === max}
        className={`group w-value-30 bg-grey-pagination rounded-r-md text-fs-26 flex items-center justify-center ${
          disabled || value === max ? "disabled" : ""
        }`}
        onClick={handleIncrease}
      >
        <span className="group-[.disabled]:opacity-20">+</span>
      </button>
    </div>
  );
};

export default InputNumber;
